import { Box, Menu, MenuItem } from '@mui/material';
import { useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useAppStore } from 'hooks/state/useAppStore';

import { TbIcon } from 'components/Figma';
import { Select } from 'components/select/Select';
import { useSelectClose } from 'components/select/SelectContext';

const TOP_BENCHMARKS = ['all', 'best', 'segment'];

const INDUSTRY_SEGMENTS = [
  'segment_accessories',
  'segment_beauty',
  'segment_fashion',
  'segment_h_and_l',
  'segment_kids',
  'segment_premium_luxury',
  'segment_shoes',
  'segment_sports',
  'segment_tech',
  'segment_underwear',
];

const ALL_BENCHMARKS = [...TOP_BENCHMARKS, ...INDUSTRY_SEGMENTS];

const getLabelForKey = (key: string, intl: IntlShape): string => {
  if (ALL_BENCHMARKS.includes(key)) {
    return intl.formatMessage({ id: `benchmarking.benchmarks.${key}` });
  }
  if (key === 'industryPeers') {
    return intl.formatMessage({ id: 'benchmarking.benchmarks.industry_peers' });
  }
  return intl.formatMessage({ id: 'benchmarking.benchmarks.all' });
};

const formatIndustrySubMenuLabel = (label: React.ReactNode): React.ReactNode => {
  if (typeof label === 'string') {
    const regex = /\s\([^)]+\)$/;
    return label.replace(regex, '');
  }
  return label;
};

interface BenchmarkSelectProps {
  selectedBenchmark?: string;
  setSelectedBenchmark?: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

interface BenchmarkOptionsProps {
  selectedValue: string;
  intl: IntlShape;
  onSelect: (value: string) => void;
}

function BenchmarkOptions({ selectedValue, intl, onSelect }: BenchmarkOptionsProps) {
  const closeSelect = useSelectClose();
  const [isIndustryMenuOpen, setIsIndustryMenuOpen] = useState(false);
  const industryMenuItemRef = useRef<HTMLButtonElement>(null);

  const handleSelect = (value: string) => {
    onSelect(value);
    setIsIndustryMenuOpen(false);
    closeSelect?.();
  };

  return (
    <>
      {TOP_BENCHMARKS.map(key => {
        const isSelected = key === selectedValue;
        return (
          <button
            key={key}
            type="button"
            className={
              isSelected
                ? 'flex h-12 min-h-12 w-full items-center justify-between bg-snow-200 px-3 py-4 text-left text-sm hover:bg-snow-200'
                : 'flex h-12 min-h-12 w-full items-center justify-between px-3 py-4 text-left text-sm hover:bg-snow-100'
            }
            onClick={() => handleSelect(key)}
          >
            <span>{getLabelForKey(key, intl)}</span>
            {isSelected && <TbIcon name="Checkmark" size="lg" />}
          </button>
        );
      })}
      <button
        ref={industryMenuItemRef}
        type="button"
        className="flex h-12 min-h-12 w-full items-center justify-between px-3 py-4 text-left text-sm hover:bg-snow-100"
        onClick={() => setIsIndustryMenuOpen(true)}
      >
        <span>{getLabelForKey('industryPeers', intl)}</span>
        <TbIcon name="ChevronRight" className="ml-2" style={{ height: '16px', width: '12px' }} />
      </button>
      <Menu
        id="industry-submenu"
        keepMounted
        open={isIndustryMenuOpen}
        onClose={() => setIsIndustryMenuOpen(false)}
        anchorEl={industryMenuItemRef.current}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {INDUSTRY_SEGMENTS.map(segmentKey => {
          const isSelected = segmentKey === selectedValue;
          return (
            <MenuItem key={segmentKey} onClick={() => handleSelect(segmentKey)} selected={isSelected}>
              <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                {formatIndustrySubMenuLabel(getLabelForKey(segmentKey, intl))}
                {isSelected && <TbIcon name="Checkmark" size="lg" />}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export function BenchmarkSelect({
  selectedBenchmark = undefined,
  setSelectedBenchmark = undefined,
  className = undefined,
  disabled = false,
}: BenchmarkSelectProps) {
  const intl = useIntl();
  const { benchmark, setBenchmark } = useAppStore();

  const selectedValue = selectedBenchmark ?? benchmark ?? 'all';

  const handleSelect = (item: string) => {
    if (setSelectedBenchmark) {
      setSelectedBenchmark(item);
    } else {
      setBenchmark(item);
    }
  };

  return (
    <div data-testid="benchmarking-peer-group-select">
      <Select
        label={intl.formatMessage({
          id: 'filter.key.selectedBenchmark',
          defaultMessage: 'Peer Group',
        })}
        value={getLabelForKey(selectedValue, intl)}
        hasValue
        disabled={disabled}
        id="benchmarking-peer-group-select"
        className={className}
      >
        <BenchmarkOptions selectedValue={selectedValue} intl={intl} onSelect={handleSelect} />
      </Select>
    </div>
  );
}
